import { memo, useCallback } from 'react';
import { ChevronRight } from 'lucide-react';
import { Flag } from '@/components/ui/flag/flag';
import type {
  CountryTimelineStatus,
  RideEntry,
  RideTimelineEntry,
  RideTimelineStatus,
  ResolvedTimeline,
} from '../types';
import { CheckeredFlagIcon } from './CheckeredFlagIcon';

const EMPTY_STATUS: CountryTimelineStatus = {
  completedCount: 0,
  liveCount: 0,
  upcomingCount: 0,
  participantsCompleted: 0,
  participantsLive: 0,
  participantsUpcoming: 0,
  status: 'upcoming',
};

function getRideStatus(ride: RideEntry, effectiveNowMs: number): RideTimelineStatus {
  if (effectiveNowMs >= ride.endUtcMs) return 'completed';
  if (effectiveNowMs >= ride.startUtcMs) return 'live';
  return 'upcoming';
}

export function VerticalTimeline({
  rideDayTimeline,
  expandedCountry,
  setExpandedCountry,
  nowPositionPct,
  effectiveNowMs,
}: {
  rideDayTimeline: ResolvedTimeline;
  expandedCountry: string | null;
  setExpandedCountry: (fn: (prev: string | null) => string | null) => void;
  nowPositionPct: number | null;
  effectiveNowMs: number;
}) {
  const handleToggle = useCallback(
    (countryCode: string) => {
      setExpandedCountry((prev) => (prev === countryCode ? null : countryCode));
    },
    [setExpandedCountry],
  );

  return (
    <div className="overflow-hidden rounded-2xl border border-stone-950/10 bg-white/70 p-2.5 shadow-sm shadow-stone-950/5 sm:p-5 md:p-6">
      <div className="space-y-1.5">
        {rideDayTimeline.entries.map((entry, idx, arr) => {
          const prev = idx > 0 ? arr[idx - 1] : null;
          const status = rideDayTimeline.statusByCountryCode[entry.countryCode] ?? EMPTY_STATUS;
          const entryStatus = status.status;
          const prevStatus = prev
            ? (rideDayTimeline.statusByCountryCode[prev.countryCode] ?? EMPTY_STATUS)
            : null;
          const isExpanded = expandedCountry === entry.countryCode;
          const showNowBefore =
            nowPositionPct !== null &&
            entryStatus === 'upcoming' &&
            (prevStatus === null || prevStatus.status !== 'upcoming');

          return (
            <VerticalTimelineRow
              key={entry.countryCode}
              entry={entry}
              status={entryStatus}
              previousStatus={prevStatus?.status ?? null}
              showNowBefore={showNowBefore}
              isExpanded={isExpanded}
              isLast={idx === arr.length - 1}
              effectiveNowMs={isExpanded ? effectiveNowMs : 0}
              onToggle={handleToggle}
            />
          );
        })}
        {nowPositionPct !== null &&
          rideDayTimeline.entries.length > 0 &&
          rideDayTimeline.entries.every(
            (e) =>
              (rideDayTimeline.statusByCountryCode[e.countryCode] ?? EMPTY_STATUS).status !==
              'upcoming',
          ) && <NowMarker className="mt-3" />}
      </div>
    </div>
  );
}

const VerticalTimelineRow = memo(function VerticalTimelineRow({
  entry,
  status,
  previousStatus,
  showNowBefore,
  isExpanded,
  isLast,
  effectiveNowMs,
  onToggle,
}: {
  entry: RideTimelineEntry;
  status: RideTimelineStatus;
  previousStatus: RideTimelineStatus | null;
  showNowBefore: boolean;
  isExpanded: boolean;
  isLast: boolean;
  effectiveNowMs: number;
  onToggle: (countryCode: string) => void;
}) {
  const segGold = previousStatus !== null && previousStatus !== 'upcoming';
  const dotStatus =
    status === 'live'
      ? 'bg-dgd-gold ring-4 ring-dgd-gold/20'
      : status === 'completed'
        ? 'bg-dgd-gold'
        : 'bg-white border-2 border-stone-950/20';
  const cardBorder =
    status === 'live'
      ? 'border-dgd-gold/50 bg-dgd-gold/10'
      : isExpanded
        ? 'border-stone-950/20 bg-white/80'
        : 'border-stone-950/10 bg-white/55 hover:border-stone-950/20 hover:bg-white/70';

  return (
    <div>
      {showNowBefore && <NowMarker className="my-3" />}
      <div className="flex min-w-0 items-stretch gap-1.5 sm:gap-3">
        <div className="flex w-9 shrink-0 flex-col items-end pt-3 sm:w-12 md:w-14">
          <p
            className={`font-mono text-xs tabular-nums leading-none ${
              status === 'live'
                ? 'text-dgd-gold-hover'
                : status === 'completed'
                  ? 'text-stone-500'
                  : 'text-stone-800'
            }`}
          >
            {entry.rides[0]?.utcLabel ?? '--:--'}
          </p>
          <p className="mt-0.5 text-[9px] uppercase tracking-wider text-stone-500">UTC</p>
          <p className="mt-1 font-mono text-[10px] tabular-nums leading-none text-stone-400">
            {entry.rides[0]?.localLabel ?? '--:--'}
          </p>
          <p className="mt-0.5 text-[9px] uppercase tracking-wider text-stone-400">local</p>
        </div>
        <div className="relative flex w-4 shrink-0 flex-col items-center sm:w-5">
          {previousStatus !== null && (
            <div
              className={`absolute left-1/2 top-0 h-1/2 w-[2px] -translate-x-1/2 ${
                segGold ? 'bg-dgd-gold' : 'bg-stone-950/15'
              }`}
            />
          )}
          {!isLast && (
            <div
              className={`absolute bottom-0 left-1/2 h-1/2 w-[2px] -translate-x-1/2 ${
                status !== 'upcoming' ? 'bg-dgd-gold' : 'bg-stone-950/15'
              }`}
            />
          )}
          <div className="relative z-10 mt-[18px]">
            <span
              className={`flex h-3 w-3 items-center justify-center rounded-full ${dotStatus} ${
                status === 'live' ? 'animate-pulse' : ''
              }`}
            >
              {status === 'completed' && (
                <svg
                  viewBox="0 0 8 8"
                  className="h-2 w-2 text-white"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M1.5 4.2 L3.2 5.8 L6.5 2.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => onToggle(entry.countryCode)}
          className={`group flex min-w-0 flex-1 flex-col rounded-2xl border px-2.5 py-3 text-left transition-colors sm:px-4 ${cardBorder}`}
        >
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            <Flag code={entry.countryCode} size="sm" className="shrink-0 rounded shadow" />
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 items-center gap-2">
                <span className="truncate text-sm font-semibold leading-tight text-stone-950">
                  {entry.countryName}
                </span>
                <span className="hidden text-[9px] font-bold tracking-[0.1em] text-stone-500 sm:inline">
                  {entry.countryCode}
                </span>
              </div>
              <p className="mt-0.5 truncate text-[11px] leading-tight text-stone-600">
                {entry.rideCountLabel} {entry.rideCount === 1 ? 'drive' : 'drives'} ·{' '}
                {entry.participantsTotalLabel} drivers
              </p>
            </div>
            <StatusPill status={status} />
            <ChevronRight
              className={`h-4 w-4 shrink-0 text-stone-500 transition-transform ${
                isExpanded ? 'rotate-90' : ''
              }`}
            />
          </div>
          {isExpanded && <ExpandedRideList entry={entry} effectiveNowMs={effectiveNowMs} />}
        </button>
      </div>
    </div>
  );
});

function StatusPill({ status }: { status: RideTimelineStatus }) {
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-full text-[10px] font-medium uppercase tracking-wider sm:gap-1 sm:px-2 sm:py-0.5 ${
        status === 'live'
          ? 'h-6 w-6 bg-dgd-gold text-white sm:h-auto sm:w-auto'
          : status === 'completed'
            ? 'h-6 w-6 bg-stone-950/10 text-stone-600 sm:h-auto sm:w-auto'
            : 'h-6 w-6 border border-stone-950/20 bg-transparent text-stone-700 sm:h-auto sm:w-auto'
      }`}
      aria-label={
        status === 'live' ? 'Driving now' : status === 'completed' ? 'Done' : 'Starting soon'
      }
    >
      {status === 'live' ? (
        <>
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
          <span className="hidden sm:inline">Driving</span>
        </>
      ) : status === 'completed' ? (
        <>
          <CheckeredFlagIcon className="h-3 w-3 sm:h-2.5 sm:w-2.5" />
          <span className="hidden sm:inline">Done</span>
        </>
      ) : (
        <>
          <span className="h-1.5 w-1.5 rounded-full border border-stone-950/40 sm:hidden" />
          <span className="hidden sm:inline">Soon</span>
        </>
      )}
    </span>
  );
}

function ExpandedRideList({
  entry,
  effectiveNowMs,
}: {
  entry: RideTimelineEntry;
  effectiveNowMs: number;
}) {
  return (
    <div className="mt-3 border-t border-stone-950/10 pt-3 animate-in fade-in slide-in-from-top-1 duration-150 motion-reduce:animate-none">
      <ul className="space-y-1.5">
        {entry.rides.map((ride) => {
          const status = getRideStatus(ride, effectiveNowMs);
          const rideDot =
            status === 'live'
              ? 'bg-dgd-gold'
              : status === 'completed'
                ? 'bg-stone-400'
                : 'border border-stone-950/30';
          return (
            <li
              key={ride.id}
              className={`flex flex-col gap-1 rounded-lg px-2 py-2 text-xs ${
                status === 'live' ? 'bg-dgd-gold/15' : ''
              }`}
            >
              <div className="flex min-w-0 items-center gap-2">
                <span className={`h-2 w-2 shrink-0 rounded-full ${rideDot}`} />
                <span className="min-w-0 flex-1 truncate font-medium text-stone-900">
                  {ride.city}
                </span>
                <span
                  className={`shrink-0 text-[10px] uppercase tracking-wider ${
                    status === 'live'
                      ? 'text-dgd-gold-hover'
                      : status === 'completed'
                        ? 'text-stone-500'
                        : 'text-stone-600'
                  }`}
                >
                  {status === 'live' ? 'Driving' : status === 'completed' ? 'Done' : 'Soon'}
                </span>
              </div>
              <div className="flex items-center justify-between gap-3 pl-4 text-[10px] text-stone-500">
                <span>{ride.participantsLabel} drivers</span>
                <span className="flex items-center gap-2 font-mono tabular-nums tracking-wider">
                  <span>{ride.utcLabel} UTC</span>
                  <span className="text-stone-400">·</span>
                  <span className="text-stone-400">{ride.localLabel} local</span>
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function NowMarker({ className = '' }: { className?: string }) {
  return (
    <div className={`flex items-center gap-3 px-2 ${className}`}>
      <div className="h-px flex-1 bg-dgd-gold/50" />
      <span className="inline-flex items-center gap-1.5 rounded-full bg-dgd-gold px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow-lg shadow-dgd-gold/30">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
        Now
      </span>
      <div className="h-px flex-1 bg-dgd-gold/50" />
    </div>
  );
}
